import { FC, useMemo } from "react";
import DataTable, { CellSize } from "../table";
import { routePaths } from "../../../Router/router";
import { Grid2, Link, MenuItem, Select, SelectChangeEvent, Stack } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { GridActionsCellItem, GridColDef, GridRowParams, useGridApiRef } from "@mui/x-data-grid-premium";
import TableCellConfigurations from "../../../Components/Table/TableCellConfigurations/table-cell-configurations";
import TableCellDueDate from "../../../Components/Table/TableCellDueDate/TableCellDueDate";
import TableCellOrderStatus from "../../../Components/Table/TableCellOrderStatus/table-cell-order-status";
import CachedIcon from "@mui/icons-material/Cached";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { useTranslation } from "react-i18next";

import React from "react";
import {
  GetEnumValueLabelMap,
  OpenLinkInNewTab,
  UserHasAcl,
  GetOrderStatusLabel,
  GroupByToMap,
} from "../../../Utils/helpers";
import { DbOrderItemInfos } from "../../../Core/Api/Types/db-order-item-infos";
import { OrderStatus } from "../../../Core/Api/Types/order-status";
import moment from "moment";
import PageFilter from "../../Misc/PageFilter";
import ListPageHeader, { ListPageHeaderLeft } from "../../Misc/ListPageHeader";
import { DbCustomerInfos } from "../../../Core/Api/Types/db-customer-infos";
import OrderPanel from "../../Panels/OrderPanel/order-panel";
import TableCellMultipleCover from "../TableCellMultipleCover/table-cell-multiple-cover";
import TableCellDate2Lines from "../TableCellDate2Lines";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { orderService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import TableCellTypography from "../TableCellTypography/table-cell-typography";
import { CancelOutlined } from "@mui/icons-material";
import TableUuids from "../table-uuids";

export enum OrderTableContext {
  Basic,
  Customers,
  Internal,
  All,
}

interface OrderTableProps {
  orders: DbOrderItemInfos[];
  context: OrderTableContext;
  isLoading?: boolean;
}

const OrderTable: FC<OrderTableProps> = (props) => {
  const { t } = useTranslation();
  const { orders, context, isLoading } = props;
  const [rows, setDataGrid] = React.useState<any[]>([]);
  const [filterIndexStatuses, setFilterIndexStatus] = React.useState<OrderStatus[]>([]);
  const [filterIndex, setFilterIndex] = React.useState<number>(-1);
  const [filterCustomer, setFilterCustomer] = React.useState<number>(-1);
  const [filterStatus, setFilterStatus] = React.useState<string>("");
  const [filterText, setFilterText] = React.useState<string>("");
  const [customers, setCustomers] = React.useState<DbCustomerInfos[]>([]);

  const [selectedOrderId, setSelectedOrderId] = React.useState<number>(-1);
  const [openPanelOrder, setOpenPanelOrder] = React.useState(false);

  const apiGrid = useGridApiRef();
  const enqueueCustomSnackbar = useCustomSnackbar();

  const aclRetry = UserHasAcl(AclTypes.aclOrderStart);
  const aclCancel = UserHasAcl(AclTypes.aclOrderCancel);

  const hidePanelOrder = () => {
    setOpenPanelOrder(false);
  };
  const showPanelOrder = (e: any) => {
    setSelectedOrderId(e);
    setOpenPanelOrder(true);
  };

  // Cols
  const columns: GridColDef[] = [
    {
      field: "reference",
      headerName: t("common:reference"),
      minWidth: CellSize.small,
      flex: CellSize.small,
      renderCell: (data: any) => (
        <Link
          color="primary.text"
          underline="hover"
          component={RouterLink}
          to={`${routePaths.ordersOverview.path}/${data.id}`}
        >
          {data.value}
        </Link>
      ),
    },
    {
      field: "date",
      headerName: t("common:date"),
      type: "date",
      minWidth: CellSize.small,
      flex: CellSize.small,
      valueGetter: (data: any, row: any) => new Date(row?.date),
      renderCell: (data: any) => <TableCellDate2Lines date={data?.row?.date} />,
    },

    {
      field: "creator",
      headerName: t("common:creator"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      renderCell: (data: any) => <TableCellTypography color={"text.secondary"} fontWeight={500} data={data.value} />,
    },
    {
      field: "customers",
      headerName: t("common:customers"),
      minWidth: CellSize.large,
      flex: CellSize.large,
      valueGetter: (value: any) => value?.map((x) => x.name).join(","),
    },
    {
      field: "titles",
      headerName: t("common:titles"),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      valueGetter: (data: any, row: any) => row?.objNames.map((x) => x.split("-")[0] || "").join(" "),
      renderCell: (data: any) => (
        <Stack py={1} mb={1}>
          {TableCellMultipleCover({ data: { ...data, value: data.row?.titles || [] } })}
        </Stack>
      ),
    }, //----> Huge memory and page is really slow with!
    {
      field: "configuration",
      headerName: t("table:headerName.configWithDot"),
      minWidth: CellSize.small,
      type: "singleSelect",
      flex: CellSize.small,
      valueOptions: GetEnumValueLabelMap(OrderProductType),
      valueGetter: (data, row) => row?.configuration.filter((n, i) => row.configuration.indexOf(n) === i),
      renderCell: (data) => <TableCellConfigurations data={data} />,
    },
    {
      field: "price",
      headerName: t("common:price"),
      type: "number",
      align: "left",
      headerAlign: "left",
      minWidth: CellSize.small,
      flex: CellSize.small,
      valueGetter: (data: any, row: any) => Number(row?.price?.split(" ")[0] || 0),
      renderCell: (data: any) => data.row?.price,
    },
    {
      field: "status",
      headerName: t("common:status"),
      type: "singleSelect",
      valueOptions: GetEnumValueLabelMap(OrderStatus, GetOrderStatusLabel).filter(
        (x) => orders.findIndex((o) => o.status === x.value) >= 0
      ),
      minWidth: CellSize.medium,
      flex: CellSize.medium,
      renderCell: (data: any) => <TableCellOrderStatus data={data} />,
    },
    {
      field: "dueDate",
      headerName: t("common:dueDate"),
      type: "date",
      valueGetter: (data: any, row: any) => new Date(row?.dueDate),
      minWidth: CellSize.small,
      flex: CellSize.small,
      renderCell: (data: any) =>
        data.row["status"] === "COMPLETED" ? "N/A" : <TableCellDueDate data={{ ...data, value: data?.row?.dueDate }} />,
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      align: "right",
      minWidth: CellSize.actionsXsmall,
      flex: CellSize.actionsXsmall,
      getActions: (params: GridRowParams) => {
        if (!params || !params.row) return [];
        let actions: any[] = [
          <GridActionsCellItem
            icon={<CancelOutlined sx={{ width: 20, height: 20 }} color="warning" />}
            label={t("common:cancel")}
            onClick={() => {
              orderService.CancelOrderById(params?.row?.id).then((response) => {
                enqueueCustomSnackbar(t("alert:success.orderCancelled"), "success");
              });
            }}
            disabled={!aclCancel || (params?.row?.status !== "RUNNING" && params?.row?.status !== "CREATED")}
            showInMenu
          />,
        ];

        if (context === OrderTableContext.Customers || context === OrderTableContext.Internal) {
          actions.push(
            <GridActionsCellItem
              icon={<PrintOutlinedIcon sx={{ width: 20, height: 20 }} />}
              label={t("table:cellLabel.printInvoice")}
              onClick={() => {
                OpenLinkInNewTab(routePaths.ordersInvoice.path + "/" + params?.row?.id);
              }}
              showInMenu
            />
          );
        }

        return actions;
      },
    },
  ];

  // Map items data to order data level
  const mapDataToGrid = (items: DbOrderItemInfos[]): any[] => {
    let grouped = GroupByToMap(items, (x) => x.orderId);
    let output: any = [];

    grouped.forEach((value: DbOrderItemInfos[], key: number | null) => {
      let firstItem = value[0];

      // Determine global status depending of items
      let status: OrderStatus = OrderStatus.CREATED;

      if (value.filter((x) => x.status === OrderStatus.FAILED).length > 0) status = OrderStatus.FAILED;
      else if (value.filter((x) => x.status === OrderStatus.RUNNING).length > 0) status = OrderStatus.RUNNING;
      else if (value.filter((x) => x.status === OrderStatus.COMPLETED).length === value.length)
        status = OrderStatus.COMPLETED;

      let titleIds: number[] = value.map((x) => x.relatedTitleId);
      let titleNames: string[] = value.filter((x) => x.titleName !== null).map((x) => x.titleName!.toLowerCase());
      let objNames: string[] = value.filter((x) => x.objectName !== null).map((x) => x.objectName!.toLowerCase());

      const distinctCustomers =
        context === OrderTableContext.Customers
          ? [
              ...new Map(
                value
                  .filter((x) => x.customer !== null)
                  .map((x) => x.customer)
                  .map((item) => [item!["id"], item])
              ).values(),
            ]
          : [];

      output.push({
        id: key,
        reference: key,
        date: firstItem.creationTime,
        creator: firstItem.ownerUserName,
        titles: titleIds.filter((n, i) => titleIds.indexOf(n) === i),
        titleNames: titleNames.filter((n, i) => titleNames.indexOf(n) === i),
        objNames: objNames.filter((n, i) => objNames.indexOf(n) === i),
        configuration: value.map((x) => x.type),
        price:
          value.reduce((accumulator: any, obj: DbOrderItemInfos) => {
            return accumulator + obj.price?.price;
          }, 0) + " €",
        status: status,
        dueDate: firstItem.dueDate !== null ? moment(firstItem.dueDate).add(10, "d") : null,
        itemsTotal: value.length,
        itemsCompleted: value.filter((x) => x.status === OrderStatus.COMPLETED).length,
        customers: distinctCustomers,
      });
    });

    return output;
  };

  // Trig open panel
  const handleShowPanelOrder = (e: any) => {
    if (showPanelOrder) showPanelOrder(e.id);
  };

  // Get counters by status
  const getCounter = (rows: any, statuses: OrderStatus[]): number => {
    return rows.filter((x: any) => statuses.map((s) => s.toString()).find((f) => f === x.status) !== undefined).length;
  };

  // Quick filter on table grid
  const changeQuickFilter = (statuses: OrderStatus[], index: number) => {
    // On reclic => remove filter
    if (filterIndex === index) {
      setFilterIndexStatus([]);
      setFilterIndex(-1);
      return;
    }

    // Apply filter on table
    setFilterIndexStatus(statuses);
    setFilterIndex(index);
  };

  // Export XSL
  const exportExcel = () => {
    apiGrid.current.exportDataAsCsv({ delimiter: GlobalConst.EXPORT_DELIMITER });
  };

  // Filter
  const handleFilterCustomer = (event: SelectChangeEvent<number>) => {
    setFilterCustomer(event.target.value as number);
  };

  const handleFilterStatus = (event: SelectChangeEvent<string>) => {
    setFilterStatus(event.target.value);
  };

  // Map data
  React.useEffect(() => {
    setDataGrid(mapDataToGrid(orders));

    switch (context) {
      case OrderTableContext.Customers:
        const distinctCustomers = [
          ...new Map(
            orders!
              .filter((x) => x.customer !== null)
              .map((x) => x.customer)
              .map((item) => [item!["id"], item!])
          ).values(),
        ];

        setCustomers(distinctCustomers);
        break;
    }
  }, [context, orders]);

  const additionalFilters = useMemo(
    () => (
      <>
        {context === OrderTableContext.Customers && (
          <Select
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            startAdornment={<HandshakeOutlinedIcon />}
            value={filterCustomer}
            onChange={(value) => handleFilterCustomer(value)}
          >
            <MenuItem value={-1}>{t("common:customer")}</MenuItem>
            {customers
              .sort((a, b) => a.name!.localeCompare(b.name!))
              .map((customer, index) => (
                <MenuItem value={customer.id} key={index}>
                  {customer.name}
                </MenuItem>
              ))}
          </Select>
        )}

        <Select
          displayEmpty
          IconComponent={ExpandMoreRoundedIcon}
          inputProps={{ "aria-label": "Without label" }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          startAdornment={<BarChartOutlinedIcon />}
          value={filterStatus}
          onChange={(value) => handleFilterStatus(value)}
        >
          <MenuItem value="" key="">
            {t("common:status")}
          </MenuItem>
          <MenuItem value="CREATED" key="CREATED">
            {t("common:pending")}
          </MenuItem>
          <MenuItem value="RUNNING" key="RUNNING">
            {t("common:processing")}
          </MenuItem>
          <MenuItem value="COMPLETED" key="COMPLETED">
            {t("common:delivered")}
          </MenuItem>
          <MenuItem value="FAILED" key="FAILED">
            {t("common:fixingUp")}
          </MenuItem>
        </Select>
      </>
    ),
    [context]
  );

  return (
    <Stack sx={{ flex: "1 1 0", minHeight: 0 }}>
      {context === OrderTableContext.Basic && (
        <ListPageHeader>
          <ListPageHeaderLeft title={t("common:orders")} />
        </ListPageHeader>
      )}

      <Grid2 container spacing={2} sx={{ pb: 2, pt: 2 }}>
        <Grid2 size={3}>
          <PageFilter
            variant="primary"
            title={getCounter(rows, [OrderStatus.RUNNING])}
            subtitle={t("common:processing")}
            icon={<CachedIcon />}
            active={filterIndex === 1}
            onClick={() => changeQuickFilter([OrderStatus.RUNNING], 1)}
            isLoading={isLoading}
          />
        </Grid2>
        <Grid2 size={3}>
          <PageFilter
            title={getCounter(rows, [OrderStatus.COMPLETED])}
            subtitle={t("common:delivered")}
            icon={<CheckIcon />}
            active={filterIndex === 2}
            onClick={() => changeQuickFilter([OrderStatus.COMPLETED], 2)}
            isLoading={isLoading}
          />
        </Grid2>
        <Grid2 size={3}>
          <PageFilter
            variant="background"
            title={getCounter(rows, [OrderStatus.CREATED, OrderStatus.QUEUED, OrderStatus.READY])}
            subtitle={t("common:pending")}
            icon={<HourglassEmptyIcon />}
            active={filterIndex === 3}
            onClick={() => changeQuickFilter([OrderStatus.CREATED], 3)}
            isLoading={isLoading}
          />
        </Grid2>
        <Grid2 size={3}>
          <PageFilter
            variant="warning"
            title={getCounter(rows, [OrderStatus.FAILED, OrderStatus.ABORTED])}
            subtitle={t("common:fixingUp")}
            icon={<SettingsOutlinedIcon />}
            active={filterIndex === 4}
            onClick={() => changeQuickFilter([OrderStatus.FAILED], 4)}
            isLoading={isLoading}
          />
        </Grid2>
      </Grid2>

      <DataTable
        loading={isLoading}
        tableKey={TableUuids.ORDER_TABLE}
        rows={rows.filter(
          (x) =>
            (filterIndexStatuses.length === 0 || filterIndexStatuses.includes(x.status!)) &&
            (filterCustomer === -1 || x.customers.filter((y) => y.id === filterCustomer).length > 0) &&
            (filterStatus === "" || x.status === filterStatus)
        )}
        columns={columns}
        getRowHeight={() => "auto"}
        additionalFilters={additionalFilters}
        checkboxSelection={false}
        disableRowSelectionOnClick
        onRowClick={handleShowPanelOrder}
        pagination={true}
        apiRef={apiGrid}
        initialState={{ pinnedColumns: { left: ["reference", "titles"], right: ["actions"] } }}
        initialColumnVisibility={{ customers: context === OrderTableContext.Customers }}
      />
      <OrderPanel open={openPanelOrder} onClose={hidePanelOrder} orderId={selectedOrderId} advanced={false} />
    </Stack>
  );
};

export default OrderTable;
